/**
 * Tone tab thumbnail strip component (Phase 3).
 *
 * Renders a horizontally-scrollable strip of region / stack thumbnails at the
 * top of the Tone tab panel. Each thumbnail is a 32×32 canvas button.
 *
 * Rules:
 *   - One button per stack (combined-mask thumbnail) + one per ungrouped region.
 *   - Strip is hidden when `state.labelMap === null` or `regionEntries` is empty.
 *   - Clicking a region thumb: `store.patch({ activeRegionUuids: [uuid], activeStackUuid: null })`.
 *   - Clicking a stack thumb: `store.patch({ activeRegionUuids: [], activeStackUuid: stackUuid })`.
 *   - Active thumb has `aria-current="true"` + `.active` CSS class.
 *   - Stack thumbs show a corner badge (member count).
 *   - Arrow keys move focus between thumbs.
 */

import type { RegionEntry, RegionStack, Store } from '../state';
import { composeRegionThumbnail, composeStackThumbnail } from '../util/stack-thumbnail';

// ── Minimal i18n surface ─────────────────────────────────────────────────────

export interface ToneThumbStripI18n {
  t(key: string, params?: Record<string, unknown>): string;
  onLocaleChange(cb: () => void): void;
}

// ── Public handle ────────────────────────────────────────────────────────────

export interface ToneThumbStripHandles {
  destroy(): void;
}

// ── Constants ────────────────────────────────────────────────────────────────

const THUMB_SIZE = 32;

// ── Factory ──────────────────────────────────────────────────────────────────

export function createToneThumbStrip(opts: {
  parent: HTMLElement;
  store: Store;
  i18n: ToneThumbStripI18n;
}): ToneThumbStripHandles {
  const { parent, store, i18n } = opts;

  const root = document.createElement('div');
  root.className = 'tone-thumb-strip-root';
  parent.appendChild(root);

  // ── Render ───────────────────────────────────────────────────────────────

  function render(): void {
    const state = store.get();
    root.replaceChildren();

    // Hide when no labelmap or no entries -- eliminates dead space in Tone tab.
    if (state.labelMap === null || state.regionEntries.length === 0) {
      root.hidden = true;
      root.dataset['empty'] = 'true';
      root.setAttribute('aria-hidden', 'true');
      return;
    }

    root.hidden = false;
    delete root.dataset['empty'];
    root.removeAttribute('aria-hidden');

    const strip = document.createElement('div');
    strip.className = 'tone-thumb-strip';
    strip.setAttribute('role', 'toolbar');
    strip.setAttribute('aria-label', i18n.t('aria.toneThumbStrip.label'));

    const {
      regionEntries,
      regionStacks,
      regionLabelPlane,
      labelMap,
      source,
      activeRegionUuids,
      activeStackUuid,
    } = state;

    const planeWidth = source?.width ?? labelMap?.width ?? 0;
    const planeHeight = source?.height ?? labelMap?.height ?? 0;

    // Build stack → member entries lookup.
    const entriesByStack = new Map<string, RegionEntry[]>();
    const ungroupedEntries: RegionEntry[] = [];
    for (const stack of regionStacks) {
      entriesByStack.set(stack.uuid, []);
    }
    for (const entry of regionEntries) {
      if (entry.stackId !== null && entriesByStack.has(entry.stackId)) {
        entriesByStack.get(entry.stackId)!.push(entry);
      } else {
        ungroupedEntries.push(entry);
      }
    }

    // ── Build list of thumb descriptors ──────────────────────────────────
    type ThumbDesc =
      | { kind: 'region'; entry: RegionEntry }
      | { kind: 'stack'; stack: RegionStack; members: RegionEntry[] };

    const descs: ThumbDesc[] = [];

    for (const stack of regionStacks) {
      const members = entriesByStack.get(stack.uuid) ?? [];
      if (members.length === 0) continue;
      descs.push({ kind: 'stack', stack, members });
    }
    for (const entry of ungroupedEntries) {
      descs.push({ kind: 'region', entry });
    }

    // ── Render thumb buttons ──────────────────────────────────────────────
    for (let i = 0; i < descs.length; i++) {
      const desc = descs[i]!;
      strip.appendChild(buildThumbBtn(desc, i, descs.length));
    }

    root.appendChild(strip);

    // ── Arrow-key navigation ──────────────────────────────────────────────
    strip.addEventListener('keydown', (event: KeyboardEvent) => {
      const btns = Array.from(strip.querySelectorAll<HTMLElement>('.tone-thumb'));
      const focusedIdx = btns.findIndex((b) => document.activeElement === b);
      if (focusedIdx === -1) return;

      let nextIdx: number | null = null;
      if (event.key === 'ArrowRight') {
        nextIdx = Math.min(focusedIdx + 1, btns.length - 1);
      } else if (event.key === 'ArrowLeft') {
        nextIdx = Math.max(focusedIdx - 1, 0);
      }

      if (nextIdx !== null && nextIdx !== focusedIdx) {
        event.preventDefault();
        btns[nextIdx]?.focus();
      }
    });

    // ── Helper: build individual thumb button ─────────────────────────────
    function buildThumbBtn(desc: ThumbDesc, index: number, total: number): HTMLButtonElement {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tone-thumb';
      btn.tabIndex = index === 0 ? 0 : -1;

      // Determine active state.
      let isActive = false;
      if (desc.kind === 'region') {
        isActive = activeRegionUuids[0] === desc.entry.uuid && activeStackUuid === null;
      } else {
        isActive = activeStackUuid === desc.stack.uuid;
      }

      if (isActive) {
        btn.classList.add('active');
        btn.setAttribute('aria-current', 'true');
      }

      // Canvas thumbnail.
      const canvas = document.createElement('canvas');
      canvas.width = THUMB_SIZE;
      canvas.height = THUMB_SIZE;
      canvas.setAttribute('aria-hidden', 'true');

      // Compute and draw thumbnail (skip when label plane is empty).
      if (regionLabelPlane.length > 0 && planeWidth > 0 && planeHeight > 0) {
        let imageData: ImageData;
        if (desc.kind === 'region') {
          imageData = composeRegionThumbnail({
            entry: desc.entry,
            regionLabelPlane,
            width: planeWidth,
            height: planeHeight,
            outputSize: THUMB_SIZE,
          });
        } else {
          imageData = composeStackThumbnail({
            members: desc.members,
            regionLabelPlane,
            width: planeWidth,
            height: planeHeight,
            outputSize: THUMB_SIZE,
          });
        }
        const ctx = canvas.getContext('2d');
        ctx?.putImageData(imageData, 0, 0);
      }

      btn.appendChild(canvas);

      // Stack badge (member count).
      if (desc.kind === 'stack') {
        const badge = document.createElement('span');
        badge.className = 'tone-thumb-badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = String(desc.members.length);
        btn.appendChild(badge);
      }

      // Accessible label.
      const ariaLabel =
        desc.kind === 'region'
          ? i18n.t('aria.toneThumb.region', { name: desc.entry.userName })
          : i18n.t('aria.toneThumb.stack', {
              name: desc.stack.userName,
              n: desc.members.length,
            });
      btn.setAttribute('aria-label', ariaLabel);
      btn.title = ariaLabel;

      // Click handler.
      btn.addEventListener('click', () => {
        if (desc.kind === 'region') {
          store.patch({ activeRegionUuids: [desc.entry.uuid], activeStackUuid: null });
        } else {
          store.patch({ activeRegionUuids: [], activeStackUuid: desc.stack.uuid });
        }
        // Also switch to tone tab if not already there.
        store.patch({ uiActiveTab: 'tone' });
      });

      void total; // suppress unused-variable lint on `total` parameter
      return btn;
    }
  }

  // Initial render.
  render();

  // Re-render on store changes.
  store.subscribe(() => render());

  // Re-render on locale changes (aria-labels).
  i18n.onLocaleChange(() => render());

  return {
    destroy(): void {
      root.remove();
    },
  };
}
